import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import { Pie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import styles from './ReportBuildMenu.module.scss';
import { Text } from '../../../../components/typography';
import { useReportLabel } from '../../../../utilities/reports';
import { bootstrapColors } from '../../utilities';
import { customColors, multiLineTruncate } from '../../../../utilities';
import CustomReportHeader from './CustomReportHeader';
import { useLabels } from '../../../../providers/LabelProvider';
import Spacer from '../../../../components/typography/Spacer';
import IncidentHistoricalStatGraph from '../IncidentHistoricalStatGraph';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';

const getReportDataQuery = loader('./CustomReports.getData.graphql');

export default function ReportBuildMenuGraphs({
  type,
  options,
  updatePDF = () => {},
  attachmentId,
}) {
  const { t } = useTranslation();
  const { group, columns = [] } = options || {};
  const getLabel = useReportLabel(group, options.model);
  const { models } = useLabels();

  const queryOptions = useMemo(
    () => ({
      model: options?.model,
      refModel: options?.refModel,
      timeRange: options?.timeRange,
      workspaces: options?.workspaces,
      type: options?.type,
      group: options?.group,
      subgroup: options?.subgroup,
      filters: options?.filters,
      where: options?.where,
      key: options?.key,
      value: options?.value,
      columns: options?.columns,
      daysBack: options?.daysBack,
      questions: options?.questions,
      template: options?.template,
    }),
    [
      options?.model,
      options?.refModel,
      options?.timeRange,
      options?.workspaces,
      options?.type,
      options?.group,
      options?.subgroup,
      options?.filters,
      options?.where,
      options?.key,
      options?.value,
      options?.columns,
      options?.daysBack,
      options?.questions,
      options?.template,
    ],
  );

  const { loading, data } = useQuery(getReportDataQuery, {
    variables: { options: [queryOptions] },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !options || options.group === 'TRIF',
  });

  const reportData = useMemo(
    () => data?.getCustomReportData?.[0]?.results ?? [],
    [data],
  );

  const breakdown = useMemo(
    () => data?.getCustomReportData?.[0]?.breakdown ?? null,
    [data],
  );
  // Resolves a breakdown value (e.g. a creator/participant user id, or a
  // label id) to its display name — reuses the same field-aware lookup the
  // main chart uses for its group labels, just keyed on the filter's field
  // instead of the group field.
  const getBreakdownLabel = useReportLabel(breakdown?.field, options.model);
  // Group label (bar id / pie slice id) -> [{ value, count }], one entry per
  // selected value of the report's active multi-value filter. Populated by
  // the backend only when that filter has 2+ values and the chart type is
  // BAR/BAR_HORIZONTAL/PIE (see computeFilterBreakdown in opas-api).
  const breakdownByLabel = useMemo(() => {
    if (!breakdown?.values?.length) return null;
    const map = new Map();
    for (const { value, results: valueResults } of breakdown.values) {
      for (const row of valueResults ?? []) {
        const key = String(row?.label ?? '');
        const count = Number(row?.count) || 0;
        if (!count) continue;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({ value, count });
      }
    }
    return map;
  }, [breakdown]);

  const lineSeries = useMemo(() => {
    const rawSeries = data?.getCustomReportData?.[0]?.series ?? [];
    return rawSeries.map((s, index) => {
      const points = (s.data ?? [])
        .map((p) => ({
          x: new Date(`${String(p.x).slice(0, 10)}T00:00:00Z`),
          y: Number(p.y) || 0,
        }))
        .sort((a, b) => a.x - b.x);

      const total = points.reduce((sum, p) => sum + p.y, 0);
      const label = getLabel?.(s.id) ?? String(s.id);
      const truncated = multiLineTruncate(label, 20);
      return {
        id: String(s.id),
        label: truncated,
        labelWithTotal: `${label} (${total})`,
        total,
        data: points,
        color: bootstrapColors[index % bootstrapColors.length],
      };
    });
  }, [data?.getCustomReportData, getLabel]);

  const seriesById = useMemo(() => {
    const m = new Map();
    for (const s of lineSeries) m.set(String(s.id), s);
    return m;
  }, [lineSeries]);

  const renderSliceTooltip = useCallback(
    ({ slice }) => {
      const entries = slice.points
        .map((p) => ({
          color: p.serieColor,
          label: seriesById.get(String(p.serieId))?.label ?? String(p.serieId),
          y: Number(p.data.yFormatted ?? p.data.y) || 0,
          id: p.id,
        }))
        .filter((e) => e.y > 0)
        .sort((a, b) => b.y - a.y);

      const MAX = 10;
      const top = entries.slice(0, MAX);
      const more = entries.length - top.length;

      return (
        <div
          style={{
            background: 'white',
            padding: '6px 8px',
            border: '1px solid #ddd',
          }}
        >
          <Text noMargin> {slice.points[0].data.xFormatted}</Text>
          {top.map((e) => (
            <div
              key={e.id}
              style={{ display: 'flex', gap: 8, alignItems: 'center' }}
            >
              <div
                style={{
                  backgroundColor: e.color,
                  borderRadius: '50%',
                  width: 12,
                  height: 12,
                }}
              />
              <Text size="sm" noMargin>
                {e.label}: {e.y}
              </Text>
            </div>
          ))}
          {more > 0 && (
            <div style={{ marginTop: 4, opacity: 0.7 }}>+{more} more</div>
          )}
        </div>
      );
    },
    [seriesById],
  );

  const pieData = useMemo(() => {
    const mapped = (reportData ?? []).map((item) => {
      // A row carries its own `title` when the backend already resolved a
      // display name (e.g. template groups, which can't be resolved from
      // `label` alone since `label` there is a stable numeric root id, not
      // a lookup key `getLabel` understands) — prefer it over the generic
      // group-field resolver.
      const l = item?.title ?? getLabel(item.label);
      const label = multiLineTruncate(l, 20);
      const raw = String(item?.label ?? '');
      return {
        id: raw,
        label,
        fullLabel: l,
        value: Number(item?.count) || 0,
        count: Number(item?.count) || 0,
      };
    });
    mapped.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

    return mapped.map((item, index) => ({
      ...item,
      color: bootstrapColors[index % bootstrapColors.length],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData, group]);

  // Keyed by the unique group id (not the display label, which can collide
  // once truncated — e.g. two different deactivated users both render as
  // "Deactivated User (ID..."). Used to look the label back up for axis
  // ticks/tooltips once the chart is indexed by id instead.
  const labelById = useMemo(() => {
    const m = new Map();
    for (const d of pieData) m.set(d.id, d);
    return m;
  }, [pieData]);

  const pivotData = useMemo(() => {
    const allData = data?.getCustomReportData ?? [];
    const results = allData[0]?.results ?? [];

    // Group by resolved label so that entries like two "Anonymous" rows
    // (one real user, one null creator) are merged into a single row.
    const byLabel = new Map();
    for (const r of results) {
      const label = r?.title ?? getLabel(r?.label);
      if (!byLabel.has(label)) {
        byLabel.set(label, { label, id: r?.label, row: { ...r } });
      } else {
        const existing = byLabel.get(label);
        const merged = { ...existing.row };
        for (const [k, v] of Object.entries(r)) {
          if (k !== 'label') {
            const n = Number(v);
            merged[k] = Number.isFinite(n)
              ? String(Number(merged[k] ?? 0) + n)
              : v;
          }
        }
        byLabel.set(label, { ...existing, row: merged });
      }
    }

    const entries = Array.from(byLabel.values());
    return {
      y: entries.map((e) => ({ label: e.label, id: e.id })),
      x: entries.map((e) => ({ ...e.row, label: e.id })),
    };
  }, [data, getLabel]);

  const tableGroups = useMemo(() => {
    const groups = Array.from(new Set(columns?.map((c) => c?.field)));
    return groups;
  }, [columns]);

  const groupedCols = useMemo(() => {
    return (tableGroups ?? []).map((g) => ({
      group: columns?.find((c) => c?.field === g)?.fieldString,
      cols: (columns ?? []).filter((c) => c?.field === g),
    }));
  }, [tableGroups, columns]);

  const flatCols = useMemo(() => {
    return groupedCols.flatMap((g) =>
      g.cols.map((c) => ({
        ...c,
        key: c.accessor ?? `${c.field}_${c.value}`,
      })),
    );
  }, [groupedCols]);

  const toNumber = (v) => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const totalsByCol = useMemo(() => {
    const rows = pivotData?.x ?? [];
    return flatCols.reduce((acc, col) => {
      acc[col.key] = rows.reduce((sum, r) => sum + toNumber(r?.[col.key]), 0);
      return acc;
    }, {});
  }, [pivotData?.x, flatCols]);

  const total = useMemo(() => {
    if (type !== 'LINE') {
      return pieData.reduce((sum, d) => sum + d.count, 0);
    } else return lineSeries.reduce((sum, s) => sum + s.total, 0);
  }, [type, pieData, lineSeries]);

  const isHorizontal = type === 'BAR_HORIZONTAL';

  // options.target is absent on every attachment saved before this field
  // existed — Number(undefined) is NaN, so hasTarget stays false and no
  // marker is rendered for historical content.
  const targetValue = Number(options?.target);
  const hasTarget = Number.isFinite(targetValue);
  const targetMarker = useMemo(
    () => ({
      value: targetValue,
      lineStyle: {
        stroke: customColors.RED,
        strokeWidth: 2,
        strokeDasharray: '6 4',
      },
      legend: `Target: ${targetValue}`,
      legendOrientation: 'horizontal',
      textStyle: { fill: customColors.RED, fontSize: 11 },
    }),
    [targetValue],
  );

  useEffect(() => {
    let data = null;
    const displayGraph = options?.group === 'TRIF' ? 'TRIF' : type;

    switch (displayGraph) {
      case 'TRIF':
        data = [];
        break;
      case 'LINE':
        data = lineSeries;
        break;
      case 'PIVOT':
        data = {
          totalsByCol,
          flatCols,
          groupedCols,
          tableGroups,
          ...pivotData,
        };
        break;
      default:
        data = pieData;
        break;
    }

    updatePDF(attachmentId, data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineSeries, pieData, type]);

  const content = useMemo(() => {
    const pie = () => (
      <div className={styles.chartContainer}>
        <Pie
          width={450}
          height={280}
          data={pieData}
          isInteractive={false}
          id="id"
          colors={{ datum: 'data.color' }}
          margin={{ left: 50, right: 30, top: 60, bottom: 50 }}
          innerRadius={0.5}
          padAngle={0.8}
          cornerRadius={3}
          borderWidth={1}
          borderColor={{ from: 'color', modifiers: [['darker', 0.5]] }}
          radialLabelsSkipAngle={10}
          radialLabelsTextColor="#333333"
          radialLabelsLinkColor={{ from: 'color' }}
          arcLinkLabelsTextColor="#6e6e73"
          sliceLabelsSkipAngle={10}
          sliceLabelsTextColor="#ffffff"
          animate={false}
          motionConfig="stiff"
          theme={{ labels: { text: { fill: 'white', fontSize: 11 } } }}
          arcLinkLabel={(d) => d.data.label}
          sliceLabel={(d) => d.data.label}
        />
        <div className={styles.pieLegend}>
          {pieData?.map((s) => {
            const breakdownParts = breakdownByLabel?.get(s.id);
            return (
              <div key={s.id}>
                <div className={styles.legendText}>
                  <span
                    className={styles.color}
                    style={{ background: s.color }}
                  />
                  <Text size="sm" noMargin>
                    {s.label} -
                  </Text>
                  <Text size="sm" noMargin weight="semiBold">
                    {s.count}
                  </Text>
                </div>
                {breakdownParts?.length ? (
                  <div style={{ paddingLeft: '1rem' }}>
                    {breakdownParts.map((p) => (
                      <Text
                        key={String(p.value)}
                        size="sm"
                        noMargin
                        color="secondary"
                      >
                        {`${getBreakdownLabel(p.value)}: ${p.count}`}
                      </Text>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );

    const line = () => {
      return (
        <div
          style={{
            minHeight: '15rem',
            width: '100%',
            overflow: 'visible',
          }}
        >
          <div style={{ height: '20rem' }}>
            <ResponsiveLine
              type="time"
              data={lineSeries}
              enableArea={false}
              animate={false}
              isInteractive={true}
              indexBy="label"
              xScale={{ type: 'time', precision: 'day', useUTC: true }}
              xFormat="time:%Y-%m-%d"
              axisBottom={{
                format: '%Y-%m-%d',
                tickRotation: -30,
                legendOffset: 45,
                legendPosition: 'middle',
              }}
              yScale={{ type: 'linear', min: 0, max: 'auto' }}
              useMesh={false}
              lineWidth={1}
              curve="monotoneX"
              enablePoints
              pointSize={4}
              pointBorderWidth={2}
              margin={{ top: 30, right: 50, bottom: 60, left: 50 }}
              axisLeft={{
                orient: 'left',
                legendOffset: -40,
                legendPosition: 'middle',
              }}
              theme={{
                axis: {
                  ticks: { line: { strokeWidth: 3 }, text: { fontSize: 11 } },
                },
              }}
              pointColor={{ theme: 'background' }}
              pointBorderColor={{ from: 'serieColor' }}
              colors={{ scheme: 'dark2' }}
              enableSlices="x"
              sliceTooltip={renderSliceTooltip}
              markers={hasTarget ? [{ ...targetMarker, axis: 'y' }] : []}
            />
          </div>
          <div className={styles.legend}>
            {lineSeries.map((s) => (
              <div key={s.id} className={styles.legendText}>
                <span
                  className={styles.color}
                  style={{ background: s.color }}
                />
                <Text size="sm" noMargin>
                  {s.label} -{' '}
                </Text>
                <Text size="sm" noMargin weight="bold">
                  {s.total}
                </Text>
              </div>
            ))}
          </div>
        </div>
      );
    };
    const h = isHorizontal
      ? pieData.length > 1
        ? 40 + pieData.length * 25
        : 70
      : 190;
    const bar = () => (
      <div
        style={{
          width: '100%',
          height: h,
        }}
      >
        <ResponsiveBar
          layout={isHorizontal ? 'horizontal' : 'vertical'}
          data={pieData}
          indexBy="id"
          keys={['count']}
          margin={{
            top: 20,
            right: 50,
            bottom: isHorizontal ? 40 : 60,
            left: isHorizontal ? 90 : 40,
          }}
          colors={(bar) => bar.data.color || '#1f77b4'}
          padding={0.2}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          borderColor={{ from: 'color', modifiers: [['darker', 0.6]] }}
          labelSkipWidth={14}
          labelSkipHeight={14}
          labelTextColor={{ from: 'color', modifiers: [['brighter', 10]] }}
          enableGridY
          axisLeft={{
            tickRotation: !isHorizontal ? 0 : -45,
            // axisLeft is the index (category) axis only in horizontal
            // layout — in vertical layout it's the value axis, and must
            // keep showing plain numbers, not a group-id label lookup.
            ...(isHorizontal && {
              format: (tick) => labelById.get(String(tick))?.label ?? tick,
            }),
          }}
          axisBottom={{
            tickRotation: isHorizontal ? 0 : -30,
            legendOffset: 30,
            legendPosition: 'middle',
            // Mirror of the above: axisBottom is the index axis only in
            // vertical layout.
            ...(!isHorizontal && {
              format: (tick) => labelById.get(String(tick))?.label ?? tick,
            }),
          }}
          tooltip={({ data, value, color }) => {
            const breakdownParts = breakdownByLabel?.get(String(data.id));
            return (
              <div
                style={{
                  background: 'white',
                  padding: '6px 8px',
                  border: '1px solid #ddd',
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background: color,
                    }}
                  />
                  <Text noMargin size="sm">
                    {data.fullLabel} - {value}
                  </Text>
                </div>
                {breakdownParts?.length ? (
                  <div style={{ marginTop: 4, paddingLeft: 18 }}>
                    {breakdownParts.map((p) => (
                      <Text
                        key={String(p.value)}
                        noMargin
                        size="sm"
                        color="secondary"
                      >
                        {`${getBreakdownLabel(p.value)}: ${p.count}`}
                      </Text>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          }}
          theme={{
            axis: {
              ticks: {
                line: { strokeWidth: 3 },
                text: { fontSize: 11 },
              },
            },
          }}
          animate={false}
          markers={
            hasTarget
              ? [{ ...targetMarker, axis: isHorizontal ? 'x' : 'y' }]
              : []
          }
        />
      </div>
    );

    const pivot = () => (
      <div
        className={styles.table}
        style={{
          '--cornerWidth': '10rem',
          '--cellWidth': '8rem',
          '--colCount': flatCols.length,
        }}
      >
        {!groupedCols?.every((g) => g?.cols?.length <= 1) && (
          <div className={styles.gridRow}>
            <div className={styles.cornerCell} />
            {groupedCols.map(({ group, cols }) => (
              <div
                key={group}
                className={styles.groupHeaderCell}
                style={{ gridColumn: `span ${cols.length}` }}
              >
                <Text noMargin weight="semiBold" textAlign="center">
                  {cols?.length > 1 ? t(group) : ' '}
                </Text>
              </div>
            ))}
          </div>
        )}
        <div className={styles.gridRow}>
          <div className={styles.cornerCell} />
          {flatCols.map((col) => (
            <div key={col.key} className={styles.headerCell}>
              <Tooltip title={t(col.title) ?? col.title}>
                <span>
                  <Text noMargin weight="semiBold" textAlign="center" truncate>
                    {t(col.title) ?? col.title}
                  </Text>
                </span>
              </Tooltip>
            </div>
          ))}
        </div>

        {(pivotData?.y ?? []).map((row) => {
          const rowKey = row?.label ?? String(row?.id);
          const rowData = pivotData?.x?.find((r) => r?.label === row?.id);

          return (
            <div key={rowKey} className={styles.gridRow}>
              <div className={styles.rowHeaderCell}>
                <Tooltip title={row?.label}>
                  <span style={{ width: '100%' }}>
                    <Text noMargin truncate>
                      {row?.label}
                    </Text>
                  </span>
                </Tooltip>
              </div>

              {flatCols.map((col) => {
                const value = rowData?.[col.key];
                return (
                  <div key={col.key} className={styles.tableCell}>
                    <Text noMargin textAlign="center">
                      {value ?? 0}
                    </Text>
                  </div>
                );
              })}
            </div>
          );
        })}

        <div className={styles.gridRow}>
          <div className={styles.rowHeaderCell}>
            <Text noMargin weight="semiBold">
              Total
            </Text>
          </div>

          {flatCols.map((col) => (
            <div key={col.key} className={styles.tableCell}>
              <Text noMargin textAlign="center" weight="semiBold">
                {totalsByCol[col.key] ?? 0}
              </Text>
            </div>
          ))}
        </div>
      </div>
    );

    switch (type) {
      case 'LINE':
        return line();
      case 'BAR':
      case 'BAR_HORIZONTAL':
        return bar();
      case 'PIVOT':
        return pivot();
      default:
        return pie();
    }
  }, [
    isHorizontal,
    lineSeries,
    pieData,
    labelById,
    renderSliceTooltip,
    type,
    t,
    flatCols,
    pivotData,
    groupedCols,
    totalsByCol,
    hasTarget,
    targetMarker,
    breakdownByLabel,
    getBreakdownLabel,
  ]);
  if (options.group === 'TRIF') {
    return <IncidentHistoricalStatGraph readOnly={true} />;
  }

  return (
    <div>
      <CustomReportHeader options={options} models={models} total={total} />

      {total === 0 && !loading && type !== 'PIVOT' ? (
        <div>
          <Text weight="semiBold" textAlign="center" color="secondary">
            No Data Found
          </Text>
          <Spacer />
        </div>
      ) : (
        content
      )}
      {!!options?.description ? (
        <Text color="secondary" weight="semiBold" textAlign="center" noMargin>
          {options.description}
        </Text>
      ) : null}
    </div>
  );
}
